import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Button, Card, Table, Td, Th } from '@/components/admin/ui';
import {
  BarList,
  ChartCard,
  EmptyState,
  KpiTile,
  RangePicker,
} from '@/components';
import type { HeroAnalyticsResponse, Range } from '@/lib/admin-types';
import { track } from '@/lib/analytics';
import { PRIMARY_HEX } from '@/lib/colors';
import { formatInt } from '@/lib/format';
import { apiJson } from '@/lib/api';

type HeroRange = Extract<Range, '7d' | '30d' | '90d'>;

const HINTS = {
  taps: 'Learning Hub hero carousel taps in the selected Pacific calendar window, compared with the previous window of the same length.',
  uniques: 'Unique Amplitude users who tapped a hero card in the window.',
  app: 'Taps from the mobile app, grouped by the card resource key.',
  web: 'Taps from the Hmonglus website, grouped by the card link.',
  chart: 'Hero taps and unique users per Pacific day.',
  cards: 'Which hero card was tapped. App rows use the resource key and title. Web rows use the card link.',
} as const;

function fmtDay(iso: string) {
  const [, m, day] = iso.split('-');
  return `${m}/${day}`;
}

function chartTooltipFormatter(value: unknown, name: unknown) {
  const v = typeof value === 'number' ? formatInt(value) : String(value);
  return [v, String(name)] as [string, string];
}

function shareLabel(taps: number, total: number) {
  if (total <= 0) return '—';
  const pct = (taps / total) * 100;
  return pct >= 10 ? `${Math.round(pct)}%` : `${pct.toFixed(1)}%`;
}

function detailLabel(source: string | null, category: string | null) {
  return source || category || '—';
}

export function HeroCardsPage() {
  const { range } = Route.useSearch();
  const navigate = useNavigate({ from: '/admin/hero-cards' });
  const qc = useQueryClient();

  useEffect(() => {
    track('Hero Cards Analytics');
  }, []);

  const heroQ = useQuery({
    queryKey: ['admin', 'hero-cards', range],
    queryFn: () =>
      apiJson<HeroAnalyticsResponse>(
        `/v1/admin/analytics/hero-cards?range=${encodeURIComponent(range)}`
      ),
  });

  const data = heroQ.data;
  const loading = heroQ.isLoading;
  const cards = data?.cards ?? [];
  const chartRows = useMemo(
    () =>
      data?.days.map((day) => ({
        ...day,
        label: fmtDay(day.date),
      })) ?? [],
    [data]
  );
  const sparkTaps = data?.days.map((day) => day.taps) ?? [];
  const sparkUniques = data?.days.map((day) => day.uniques) ?? [];
  const hasChart = chartRows.some((day) => day.taps > 0 || day.uniques > 0);

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-nova-foreground">Hero Cards</h1>
          <p className="mt-1 text-sm text-nova-muted-foreground">
            Learning Hub hero carousel taps, and which card was tapped.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <RangePicker
            value={range}
            options={['7d', '30d', '90d']}
            onChange={(next) => {
              void navigate({ search: { range: next } });
            }}
          />
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              void qc.invalidateQueries({ queryKey: ['admin', 'hero-cards'] });
            }}
          >
            Refresh
          </Button>
        </div>
      </div>

      {heroQ.error && (
        <p className="text-sm text-red-600">
          {(heroQ.error as Error).message}
          <button type="button" className="ml-2 underline" onClick={() => void heroQ.refetch()}>
            Retry
          </button>
        </p>
      )}
      {data?.error && <p className="text-sm text-red-600">{data.error}</p>}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiTile
          label="Hero taps"
          value={data?.taps ?? 0}
          delta={data?.delta_taps}
          spark={sparkTaps.slice(-14)}
          loading={loading}
          tooltip={HINTS.taps}
        />
        <KpiTile
          label="Unique users"
          value={data?.uniques ?? 0}
          delta={data?.delta_uniques}
          spark={sparkUniques.slice(-14)}
          loading={loading}
          tooltip={HINTS.uniques}
        />
        <KpiTile label="App taps" value={data?.app_taps ?? 0} loading={loading} tooltip={HINTS.app} />
        <KpiTile label="Web taps" value={data?.web_taps ?? 0} loading={loading} tooltip={HINTS.web} />
      </div>

      <ChartCard title="Daily hero taps" tooltip={HINTS.chart} loading={loading} height={280}>
        {!hasChart ? (
          <EmptyState title="No hero taps in this range" />
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartRows} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
              <YAxis
                allowDecimals={false}
                width={44}
                tick={{ fontSize: 11 }}
                tickFormatter={(value) => formatInt(Number(value))}
              />
              <Tooltip formatter={chartTooltipFormatter} />
              <Legend />
              <Bar dataKey="taps" name="Taps" fill={PRIMARY_HEX} radius={[4, 4, 0, 0]} />
              <Line
                type="monotone"
                dataKey="uniques"
                name="Unique users"
                stroke="#b45309"
                strokeWidth={2}
                dot={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </ChartCard>

      <div className="grid gap-6 lg:grid-cols-5">
        <Card padding="md" className="cursor-help lg:col-span-2" title={HINTS.cards}>
          <h2 className="mb-3 text-sm font-semibold text-nova-foreground">Taps by card</h2>
          {loading ? (
            <div className="h-40 animate-pulse rounded-lg bg-nova-muted" />
          ) : (
            <BarList
              data={cards.map((card) => ({
                key: `${card.surface}:${card.key}`,
                count: card.taps,
                label: card.title,
              }))}
              total={data?.taps}
              emptyLabel="No hero card taps in this range"
            />
          )}
        </Card>

        <Card padding="md" className="cursor-help lg:col-span-3" title={HINTS.cards}>
          <h2 className="mb-3 text-sm font-semibold text-nova-foreground">Which card was tapped</h2>
          {loading ? (
            <div className="h-40 animate-pulse rounded-lg bg-nova-muted" />
          ) : cards.length === 0 ? (
            <EmptyState title="No hero card taps in this range" />
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <thead>
                  <tr>
                    <Th>Card</Th>
                    <Th>Surface</Th>
                    <Th>Source</Th>
                    <Th className="text-right">Taps</Th>
                    <Th className="text-right">Users</Th>
                    <Th className="text-right">Share</Th>
                  </tr>
                </thead>
                <tbody>
                  {cards.map((card) => (
                    <tr key={`${card.surface}:${card.key}`}>
                      <Td>
                        <p className="font-medium text-nova-foreground">{card.title}</p>
                        <p className="mt-0.5 break-all text-xs text-nova-muted-foreground">{card.key}</p>
                      </Td>
                      <Td className="capitalize">{card.surface}</Td>
                      <Td>{detailLabel(card.source, card.category)}</Td>
                      <Td className="text-right tabular-nums">{formatInt(card.taps)}</Td>
                      <Td className="text-right tabular-nums">{formatInt(card.uniques)}</Td>
                      <Td className="text-right tabular-nums">
                        {shareLabel(card.taps, data?.taps ?? 0)}
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
          {!loading && (data?.unattributed_taps ?? 0) > 0 ? (
            <p className="mt-4 text-xs text-nova-muted-foreground">
              {formatInt(data?.unattributed_taps ?? 0)} taps did not include a card name.
            </p>
          ) : null}
        </Card>
      </div>
    </div>
  );
}

export const Route = createFileRoute('/admin/hero-cards')({
  validateSearch: (raw: Record<string, unknown>): { range: HeroRange } => {
    const value = raw.range;
    if (value === '7d' || value === '30d' || value === '90d') return { range: value };
    return { range: '30d' };
  },
  component: HeroCardsPage,
});
